/**
 * Банк фраз (`summary_completion_bank`) как на экзамене (T3, виды Reading как на engnovate, 29.10.2026):
 * абзац резюме с пропусками по вёрстке с сервера, под ним рамка фраз A–J. Фразу ставят в пропуск
 * касанием — пропуск, затем фраза (телефон и мышь) — или перетаскиванием на компьютере.
 *
 * ⚠️ Значение ответа — ТЕКСТ ФРАЗЫ, как у выпадающего списка прежней карточки (`LegacyTaskCard`):
 * проверка на сервере и сданные попытки не меняются. Буква — только подпись на экране.
 *
 * Без сходящейся вёрстки или без фраз — прежняя карточка с выпадающим списком (`bankLayout` → null).
 */
import type { StudentQuestion, StudentTask } from '../api/ieltsMock';
import { serverLayout, type ServerLayout } from './serverLayout';

const LETTERS = 'ABCDEFGHIJKLMNOP';

export type BankPhrase = { letter: string; text: string };

/** Вёрстка банка, если по ней можно рисовать: вид банка, фразы есть, вёрстка сходится с вопросами. */
export function bankLayout(task: StudentTask): ServerLayout | null {
  if (task.type !== 'summary_completion_bank' || !task.options?.length) return null;
  return serverLayout(task.layout, task.questions);
}

/** Фразы рамки с буквами по порядку: A, B, C… (дальше P букв нет — фраза не показывается). */
export function bankPhrases(options: string[] | undefined): BankPhrase[] {
  return (options ?? []).slice(0, LETTERS.length).map((text, i) => ({ letter: LETTERS[i], text }));
}

/** Буква фразы в пропуске; '' — пропуск пуст или такой фразы в рамке нет. */
export function phraseLetter(phrases: BankPhrase[], value: string): string {
  if (!value) return '';
  return phrases.find((p) => p.text === value)?.letter ?? '';
}

/** Вопросы в порядке пропусков (по месту в вёрстке). */
export function gapOrder(layout: ServerLayout): StudentQuestion[] {
  return [...layout.bySlot.entries()].sort((a, b) => a[0] - b[0]).map(([, q]) => q);
}

/**
 * Следующий пустой пропуск после `from` (по кругу, `from` не в счёт); без `from` — первый пустой.
 * null — все заполнены: фраза никуда не встанет, пока ученик не выберет пропуск сам.
 */
export function nextEmptyGap(order: StudentQuestion[], answers: Record<string, string>, from: string | null): string | null {
  const n = order.length;
  const start = from === null ? -1 : order.findIndex((q) => q.id === from);
  for (let k = 1; k <= n; k++) {
    const q = order[(start + k) % n];
    if (q.id !== from && !(answers[q.id] ?? '')) return q.id;
  }
  return null;
}
